<template>
  <RevCard class="flex items-center p-28">
    <div class="flex flex-1 items-center">
      <Flag
        class="mr-12"
        :country-code="sellerStore.referenceMarket as MarketCountryCode"
        :size="'l'"
      />

      <p class="body-1-bold">
        {{
          i18n(translations.referenceMarket, {
            country: i18n.country(sellerStore.referenceMarket || ''),
          })
        }}
      </p>
    </div>

    <RevButton
      size="small"
      variant="secondary"
      @click="handleOpenReferenceMarketModal"
    >
      {{ i18n(translations.referenceMarketLink) }}
    </RevButton>
  </RevCard>

  <RevDialog
    :name="MODAL_NAME"
    :title="i18n(translations.referenceMarketModalTitle)"
  >
    <template #body>
      <div class="text-action-danger-hi mb-24">
        {{ i18n(translations.referenceMarketModalSubtitle) }}
      </div>

      <ul class="grid list-none grid-cols-3 gap-12">
        <li v-for="market in sellerStore.activeMarkets" :key="market">
          <RevRadio
            :id="market"
            v-model="selectedMarket"
            name="market"
            :value="market"
            variant="full"
          >
            <template #label>
              {{ i18n.country(market) }}
            </template>
          </RevRadio>
        </li>
      </ul>
    </template>

    <template #footer>
      <RevButton
        :disabled="!selectedMarket"
        full-width="always"
        :loading="isLoading"
        variant="primary"
        @click="onChangeReferenceMarket"
      >
        {{ i18n(translations.buttonSubmit) }}
      </RevButton>
    </template>
  </RevDialog>
</template>

<script setup lang="ts">
import { ref } from 'vue'

import { MarketCountryCode, merchantsAPI } from '@backmarket/http-api'
import Flag from '@backmarket/nuxt-layer-base/components/Flag/Flag.vue'
import { $httpFetch } from '@backmarket/nuxt-module-http/$httpFetch'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useTheToast } from '@backmarket/nuxt-module-toast/useTheToast'
import { RevButton } from '@ds/components/Button'
import { RevCard } from '@ds/components/Card'
import { RevDialog } from '@ds/components/Dialog'
import { closeModal, openModal } from '@ds/components/ModalBase'
import { RevRadio } from '@ds/components/Radio'

import { useSellerInfoStore } from '~/scopes/auth/stores/sellerInfoStore'

import translations from './ReferenceMarket.translations'

const MODAL_NAME = 'pricing-tool.reference-market'

const i18n = useI18n()
const sellerStore = useSellerInfoStore()
const { openErrorToast } = useTheToast()

const isLoading = ref(false)
const emit = defineEmits(['change-market'])

const props = defineProps<{
  referenceMarket: string
}>()

function handleOpenReferenceMarketModal() {
  openModal(MODAL_NAME)
}

const selectedMarket = ref(props.referenceMarket)

async function onChangeReferenceMarket() {
  if (selectedMarket.value !== sellerStore.referenceMarket) {
    try {
      isLoading.value = true

      await $httpFetch(merchantsAPI.patchPricingRulesReferenceMarket, {
        body: {
          referenceMarket: selectedMarket.value,
        },
      })

      await sellerStore.fetchSellerInfo()
      emit('change-market')

      closeModal(MODAL_NAME)
    } catch {
      closeModal(MODAL_NAME)
      openErrorToast()
    } finally {
      isLoading.value = false
    }
  } else {
    closeModal(MODAL_NAME)
  }
}
</script>
